import React, {useEffect, useState} from 'react'
import dashboardService from '../../services/dashboard.service'
import Loading from '../common/Loading'

const Dashboard = () => {
    const [stats, setStats] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        const fetchStats = async () => {
            try {
                setLoading(true);
                const response = await dashboardService.getStats();
                setStats(Array.isArray(response?.data) ? response.data : []);
            } catch (err) {
                console.error("Error fetching stats:", err);
                setError("Failed to load dashboard data");
                setStats([]);
            } finally {
                setLoading(false);
            }
        };
        fetchStats();
    }, []);

    // Safe data processing with icons
    const getCategoryStats = (category) => {
        if (!Array.isArray(stats)) return [];

        return stats.filter(stat => {
            if (!stat?.statsName) return false;
            const name = stat.statsName.toLowerCase();

            switch(category) {
                case 'clinic':
                    return name.includes('clinic') && !name.includes('patient');
                case 'patient':
                    return name.includes('patient');
                case 'reminders':
                    return name.includes('reminder') || name.includes('recall') || name.includes('appointment');
                case 'communications':
                    return (name.includes('sms') || name.includes('email') || name.includes('call')) &&
                        !name.includes('reminder');
                case 'content':
                    return name.includes('article') || name.includes('campaign') || name.includes('survey');
                default:
                    return false;
            }
        });
    };

    // Get icon for individual stat cards
    const getStatIcon = (statName) => {
        if (!statName) return 'bi-graph-up';
        if (statName.includes('ACTIVE')) return 'bi-check-circle';
        if (statName.includes('FAILED')) return 'bi-exclamation-triangle';
        if (statName.includes('SENT')) return 'bi-send-check';
        if (statName.includes('RECALL')) return 'bi-arrow-repeat';
        if (statName.includes('APPOINTMENT')) return 'bi-calendar-date';
        if (statName.includes('REMINDER')) return 'bi-alarm';
        if (statName.includes('SURVEY')) return 'bi-clipboard-data';
        if (statName.includes('ARTICLE')) return 'bi-journal-text';
        return 'bi-graph-up';
    };

    // Format display names
    const formatName = (name) => {
        if (!name) return '';
        return name
            .replace(/_/g, ' ')
            .replace(/(^\w|\s\w)/g, m => m.toUpperCase());
    };

    if (loading) {
        return (
            <Loading />
        );
    }

    if (error) {
        return (
            <div className="alert alert-danger mx-3 my-5">
                <i className="bi bi-exclamation-triangle-fill me-2"></i>
                {error}
            </div>
        );
    }

    if (!stats.length) {
        return (
            <div className="text-center py-5">
                <i className="bi bi-database-exclamation fs-1 text-muted"></i>
                <h4 className="mt-3">No data available</h4>
                <button
                    className="btn btn-primary mt-3"
                    onClick={() => window.location.reload()}
                >
                    <i className="bi bi-arrow-clockwise me-2"></i>
                    Refresh Data
                </button>
            </div>
        );
    }

    return (
        <div className="container py-4">
            {/* Dashboard Header */}
            <div className="d-flex align-items-center mb-4">
                <div className="bg-primary bg-opacity-10 p-3 rounded me-3">
                    <i className="bi bi-speedometer2 fs-2 text-primary"></i>
                </div>
                <div>
                    <h4 className="mb-0">Admin Dashboard</h4>
                    <p className="text-muted mb-0">Overview of all clinic activities and metrics</p>
                </div>
            </div>

            {/* Overview Cards */}
            <div className="row mb-5 g-4">
                {[
                    {
                        title: 'Total Clinics',
                        key: 'CLINICS',
                        icon: 'bi-building',
                        color: 'primary',
                        desc: 'All registered clinics'
                    },
                    {
                        title: 'Active Clinics',
                        key: 'ACTIVE_CLINICS',
                        icon: 'bi-check-circle',
                        color: 'success',
                        desc: 'Currently active locations'
                    },
                    {
                        title: 'Total Patients',
                        key: 'PATIENTS',
                        icon: 'bi-people',
                        color: 'info',
                        desc: 'All registered patients'
                    },
                    {
                        title: 'Active Reminders',
                        key: 'APPOINTMENT_REMINDERS',
                        icon: 'bi-alarm',
                        color: 'warning',
                        desc: 'Pending reminders'
                    }
                ].map((metric, i) => {
                    const stat = stats.find(s => s.statsName === metric.key);
                    return (
                        <div key={i} className="col-md-6 col-lg-3">
                            <div className={`card card-dashboard border-start border-${metric.color} border-4 h-100 shadow-sm`}>
                                <div className="p-2"></div>
                                <div className="card-body">
                                    <div className="d-flex align-items-center">
                                        <i className={`bi ${metric.icon} fs-2 text-${metric.color} me-3`}></i>
                                        <div>
                                            <h5 className="text-muted mb-1"><strong>{metric.title}</strong></h5>
                                            <h4 className="mb-0">{stat?.statsValue || '0'}</h4>
                                            <small className="text-muted">{metric.desc}</small>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    );
                })}
            </div>

            {/* Detailed Sections */}
            {[
                {
                    title: 'Clinic Statistics',
                    category: 'clinic',
                    icon: 'bi-hospital',
                    color: 'primary',
                    desc: 'Detailed clinic operations and status metrics'
                },
                {
                    title: 'Patient Statistics',
                    category: 'patient',
                    icon: 'bi-people-fill',
                    color: 'success',
                    desc: 'Patient demographics and engagement'
                },
                {
                    title: 'Reminders & Appointments',
                    category: 'reminders',
                    icon: 'bi-calendar-check',
                    color: 'warning',
                    desc: 'All scheduled reminders and recalls'
                },
                {
                    title: 'Communications',
                    category: 'communications',
                    icon: 'bi-chat-left-text',
                    color: 'info',
                    desc: 'Messaging and notification statistics'
                },
                {
                    title: 'Content & Campaigns',
                    category: 'content',
                    icon: 'bi-file-earmark-text',
                    color: 'success',
                    desc: 'Articles, surveys and marketing campaigns'
                }
            ].map((section, i) => {
                const sectionStats = getCategoryStats(section.category);

                return sectionStats.length > 0 && (
                    <section key={i} className="mb-5">
                        <div className="d-flex align-items-center mb-4">
                            <div className={`bg-${section.color} bg-opacity-10 p-3 rounded me-3`}>
                                <i className={`bi ${section.icon} fs-2 text-${section.color}`}></i>
                            </div>
                            <div>
                                <h4 className="mb-1">{section.title}</h4>
                                <p className="text-muted mb-0">{section.desc}</p>
                            </div>
                        </div>

                        <div className="row g-4">
                            {sectionStats.map((stat, j) => (
                                <div key={j} className="col-sm-6 col-md-4 col-lg-3">

                                    <div className="card card-dashboard h-100 shadow-sm">
                                        <div className="card-body text-center py-3">
                                            <div className="card-title text-muted mb-2">
                                                <i className={`bi ${getStatIcon(stat.statsName)} mb-2 text-${section.color}`}></i> {formatName(stat.statsName)}
                                            </div>
                                            <hr/>
                                            <h4 className={`card-text fw-bold ${
                                                stat.statsValue === '0' || stat.statsValue === '0 (0%)' ?
                                                    'text-muted' : `text-${section.color}`
                                            }`}>
                                                {stat.statsValue}
                                            </h4>
                                        </div>
                                        <div className="card-footer bg-transparent border-top-0 pt-0 text-end">
                                            <small className="text-muted">
                                                <i className="bi bi-clock"></i> Updated: {stat.modifiedAt}
                                            </small>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </section>
                );
            })}
        </div>
    );
};
export default Dashboard